import * as React from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useOrderKentuckyPO } from '@/hooks/usePOMutations'
import { usePO } from '@/hooks/usePOs'
import { useUIStore } from '@/store/uiStore'

// Confirmation modal for transmitting an APPROVED Kentucky PO to Kentucky
// Horsewear (Arana). Kentucky generates the order reference for us, so there's
// nothing for the buyer to type — they review the line/EAN summary and send.
// A line without a barcode can't be matched at Kentucky, so Send is blocked
// until every line has one (the backend enforces the same, this is just early
// feedback).
export function OrderKentuckyModal() {
  const activeModal = useUIStore((s) => s.activeModal)
  const activePO = useUIStore((s) => s.activePO)
  const setActiveModal = useUIStore((s) => s.setActiveModal)
  const open = activeModal === 'orderKentucky'

  const { data: po } = usePO(open ? activePO : null)
  const mutation = useOrderKentuckyPO()
  const busy = mutation.isPending

  React.useEffect(() => {
    if (open) mutation.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function close() {
    if (!busy) setActiveModal(null)
  }

  const items = po?.items ?? []
  const missingBarcode = items.filter((i) => !(i.barcode ?? '').trim())
  const totalUnits = items.reduce((sum, i) => sum + (i.qty_ordered ?? 0), 0)
  const canSend = items.length > 0 && missingBarcode.length === 0 && !busy

  const rawPoNum = (po?.po_number ?? '').replace(/^#+/, '')
  const poLabel = rawPoNum ? `#${rawPoNum}` : activePO ?? ''
  const recipient = (po?.supplier ?? '').trim() || (po?.vendor ?? '').trim()

  async function onSend() {
    if (!activePO) return
    try {
      const result = await mutation.mutateAsync(activePO)
      const ref = result.arana_order_reference || activePO
      if (result.status === 'PARTIAL_ORDERED') {
        toast.success(`Sent to Kentucky · order ${ref}`)
        toast.warning(
          'Some items failed to sync to Shopify. Use Retry Sync from the PO menu once resolved.',
          { duration: 6000 }
        )
      } else {
        toast.success(`Sent to Kentucky · order ${ref}`)
      }
      close()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send order to Kentucky')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] flex flex-col gap-0 overflow-hidden p-0 sm:max-w-[560px]"
        showCloseButton={false}
      >
        <DialogHeader className="border-b border-[var(--line-warm)] px-5 py-3.5 sm:text-left">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-base font-semibold">Order with Kentucky</DialogTitle>
            {poLabel && <span className="text-xs text-[var(--mute)]">{poLabel}</span>}
            {recipient && <span className="text-xs text-[var(--mute)]">· {recipient}</span>}
            <div className="flex-1" />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={close}
              disabled={busy}
              aria-label="Close"
            >
              ✕
            </Button>
          </div>
          <DialogDescription className="sr-only">
            Review the line items and send this purchase order directly to Kentucky
            Horsewear.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 p-5">
          <p className="text-sm text-[var(--mute)]">
            This sends {items.length} {items.length === 1 ? 'line' : 'lines'} ({totalUnits}{' '}
            {totalUnits === 1 ? 'unit' : 'units'}) directly to Kentucky Horsewear. They
            return the order reference automatically.
          </p>

          {missingBarcode.length > 0 && (
            <div
              className="rounded-md border border-[var(--line-warm)] bg-[var(--status-rejected-bg)] px-3 py-2 text-xs text-[var(--status-rejected-fg)]"
              data-testid="kentucky-missing-barcode"
            >
              {missingBarcode.length}{' '}
              {missingBarcode.length === 1 ? 'line has' : 'lines have'} no barcode/EAN and
              can&apos;t be sent. Add a barcode to every line first.
            </div>
          )}

          <div className="max-h-64 overflow-auto rounded-md border border-[var(--line-warm)]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[var(--surface-header)] text-[var(--mute)]">
                <tr>
                  <th className="tbl-th text-left">Item</th>
                  <th className="tbl-th text-left">SKU</th>
                  <th className="tbl-th text-left">EAN (barcode)</th>
                  <th className="tbl-th text-right">Qty</th>
                </tr>
              </thead>
              <tbody>
                {items.map((i) => {
                  const missing = !(i.barcode ?? '').trim()
                  return (
                    <tr key={i.po_item_id} className="border-t border-[var(--line-warm)]">
                      <td className="px-3 py-1.5">
                        {i.product_title || i.sku || `Item ${i.po_item_id}`}
                        {i.variant_title && (
                          <span className="text-[var(--mute)]"> · {i.variant_title}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        <span className="font-mono">{i.sku || '—'}</span>
                      </td>
                      <td className="px-3 py-1.5">
                        {missing ? (
                          <span className="text-[var(--status-rejected-fg)]">missing</span>
                        ) : (
                          <span className="font-mono">{i.barcode}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{i.qty_ordered}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={onSend}
              disabled={!canSend}
              data-testid="order-kentucky-submit"
            >
              {busy ? 'Sending…' : 'Send order →'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
